import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface SidebarContextType {
  /** Whether the sidebar is collapsed to icon-only mode */
  isCollapsed: boolean;
  /** @deprecated No longer used - user controls sidebar state */
  canExpand: boolean;
  /** Collapse the sidebar */
  collapseSidebar: () => void;
  /** Expand the sidebar */
  expandSidebar: () => void;
  /** Toggle sidebar collapsed state */
  toggleSidebar: () => void;
  /** @deprecated No longer used - kept for backward compatibility */
  setCanExpand: (canExpand: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const collapseSidebar = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const expandSidebar = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  // Kept for backward compatibility - no longer auto-collapses
  const setCanExpand = useCallback((_allowed: boolean) => {
    // No-op: user controls sidebar state now
  }, []);

  return (
    <SidebarContext.Provider value={{ isCollapsed, canExpand: true, collapseSidebar, expandSidebar, toggleSidebar, setCanExpand }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext;
